import * as React from 'react'
import styled from 'styled-components'
import 'mapbox-gl/dist/mapbox-gl.css'

import * as map from '@owl-nest/location-map'
import * as plume from '@ulule/owl-kit-components/next'

import { BaseLocationPickerProps, Location } from './type'
import { LocationCard } from './LocationCard'

export type MapPickerProps = {
  className?: string
  mapboxApiKey?: string
  center?: { lon: number; lat: number }
  onError?: (error: any) => void
} & BaseLocationPickerProps

function MapPickerComponent({
  className,
  onError,
  center,
  mapboxApiKey,
  locations,
  selected,
  onSelect,
}: MapPickerProps) {
  const selectedIndex = locations.findIndex((location) => location.id === selected)

  return (
    <Box className={className}>
      <Map
        options={{
          accessToken: mapboxApiKey,
          style: 'mapbox://styles/mapbox/streets-v12',
          viewport: map.getViewport(locations.map((location) => location.coordinates)),
          logoPosition: 'top-left',
        }}
        onError={onError}
      >
        {locations
          .filter((location) => {
            return location.timetable.some((time) => (time.hours ?? []).length !== 0)
          })
          .map((location, index) => {
            return (
              <map.Marker
                key={`${location.coordinates.lon}:${location.coordinates.lat}`}
                options={{
                  lngLat: location.coordinates,
                }}
              >
                <Pin
                  size={32}
                  active={location.id === selected}
                  index={index + 1}
                  onClick={() => onSelect?.(location)}
                />
              </map.Marker>
            )
          })}
        {center && (
          <map.Marker
            key={`${center.lon}:${center.lat}`}
            options={{
              lngLat: center,
            }}
          >
            <CenterPin size={32} />
          </map.Marker>
        )}
      </Map>
      {selected && selectedIndex !== -1 && (
        <LocationCard location={locations[selectedIndex]} index={selectedIndex + 1} />
      )}
    </Box>
  )
}

const Box = styled.div`
  border: 1px solid ${plume.COLORS.BORDER_PRIMARY};
`

const Map = styled(map.Map)`
  height: 384px;
`

export const MapPicker = styled(MapPickerComponent)`
  ${plume.Button} {
    margin-top: 24px;
    width: 100%;
  }
`

type PinProps = Omit<plume.glyphs.IconProps, 'children' | 'name'> & { index: number; active?: boolean }

const Pin = styled(function Pin({ index, active, ...iconProps }: PinProps) {
  return (
    <plume.glyphs.Icon {...iconProps} viewBox="0 0 34 42" name="location-pin">
      <Group>
        <PinPath
          $active={active}
          d="M16.6564 0C7.53908 0 0.121094 7.41765 0.121094 16.5363C0.121094 25.4672 15.4952 40.8466 16.1508 41.4971L16.6565 42L17.1635 41.4971C17.8179 40.8466 33.1928 25.4672 33.1928 16.5363C33.1927 7.41765 25.775 0 16.6564 0Z"
        />
        <PinIndex textAnchor="middle" x={17} y={24}>
          {index}
        </PinIndex>
      </Group>
    </plume.glyphs.Icon>
  )
})``

type CenterPinProps = Omit<plume.glyphs.IconProps, 'children' | 'name'>

const CenterPin = styled(function Pin(iconProps: CenterPinProps) {
  return (
    <plume.glyphs.Icon {...iconProps} viewBox="0 0 43 43" name="location-pin">
      <Group>
        <path
          d="M22.1719 1.34375C31.4485 1.34375 38.9688 9.16176 38.9688 18.8057C38.9688 24.6755 35.9809 30.37 31.0733 35.7554C29.4027 37.5887 27.6193 39.2573 25.8353 40.7342C24.7587 41.6254 23.9116 42.2614 23.4067 42.6113C22.6589 43.1296 21.6848 43.1296 20.9371 42.6113C20.4321 42.2614 19.5851 41.6254 18.5085 40.7342C16.7244 39.2573 14.9411 37.5887 13.2704 35.7554C8.3628 30.37 5.375 24.6755 5.375 18.8057C5.375 9.16176 12.8952 1.34375 22.1719 1.34375ZM22.1719 5.97221C15.3541 5.97221 9.82718 11.718 9.82718 18.8057C9.82718 23.2448 12.3034 27.9643 16.5025 32.5722C18.0192 34.2366 19.6509 35.7633 21.2819 37.1134C21.5919 37.37 21.8893 37.6098 22.1719 37.832C22.4545 37.6098 22.7519 37.37 23.0619 37.1134C24.6928 35.7633 26.3245 34.2366 27.8412 32.5722C32.0403 27.9643 34.5166 23.2448 34.5166 18.8057C34.5166 11.718 28.9897 5.97221 22.1719 5.97221ZM22.1719 11.4422C26.0837 11.4422 29.2549 14.739 29.2549 18.8057C29.2549 22.8725 26.0837 26.1692 22.1719 26.1692C18.26 26.1692 15.0888 22.8725 15.0888 18.8057C15.0888 14.739 18.26 11.4422 22.1719 11.4422ZM22.1719 16.0707C20.7189 16.0707 19.541 17.2952 19.541 18.8057C19.541 20.3162 20.7189 21.5407 22.1719 21.5407C23.6248 21.5407 24.8027 20.3162 24.8027 18.8057C24.8027 17.2952 23.6248 16.0707 22.1719 16.0707Z"
          fill={plume.COLORS.PRIMARY_BLUE_900}
        />{' '}
      </Group>
    </plume.glyphs.Icon>
  )
})``

const Group = styled.g`
  cursor: pointer;
`

const PinPath = styled.path<{ $active?: boolean }>`
  fill: ${({ $active }) => ($active ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_BLUE_900)};
`

const PinIndex = styled.text`
  ${plume.styles.copy._largeStyle};
  font-weight: 600;
  fill: ${plume.COLORS.PRIMARY_GREY_000};
`
